<template>
  <div class="columns">
    <b-field
      label="Date of birth"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('date_of_birth') }"
      :message="hasError('date_of_birth')"
    >
      <b-datepicker
        v-model="student.personalInformation.date_of_birth"
        locale="en-GB"
        :show-week-number="true"
        placeholder="Click to select..."
        icon="calendar-today"
        :icon-right="student.date_of_birth ? 'close-circle' : ''"
        icon-right-clickable
        @icon-right-click="clearDate"
        trap-focus
      >
      </b-datepicker>
    </b-field>

    <b-field
      label="Country of birth"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('birth_country') }"
      :message="hasError('birth_country')"
    >
      <b-autocomplete
        open-on-focus
        rounded
        v-model="student.personalInformation.birth_country"
        :data="filteredCountries('birth_country')"
        icon="magnify"
        clearable
        @select="(option) => (student.birth_country = option)"
      >
        <template #empty>No results found</template>
      </b-autocomplete>
    </b-field>

    <b-field
      label="Gender"
      class="column col-sm-12 col-md-6 col-lg-3"
      :type="{ 'is-danger': hasError('gender') }"
      :message="hasError('gender')"
    >
      <b-select
        v-model="student.personalInformation.gender"
        placeholder="Select a gender"
      >
        <option v-for="option in genderOptions" :value="option" :key="option">
          {{ option }}
        </option>
      </b-select>
    </b-field>

    <b-field
      label="Marital status"
      class="column col-sm-12 col-md-6 col-lg-3"
      :type="{ 'is-danger': hasError('married') }"
      :message="hasError('married')"
    >
      <b-select
        v-model="student.personalInformation.married"
        placeholder="Select a martial status"
      >
        <option v-for="option in maritalStatus" :value="option" :key="option">
          {{ option }}
        </option>
      </b-select>
    </b-field>

    <b-field
      label="How did you find this student? "
      class="column col-sm-12 col-md-6 col-lg-3"
      :type="{ 'is-danger': hasError('student_referal') }"
      :message="hasError('student_referal')"
    >
      <b-select
        v-model="student.personalInformation.student_referal"
        placeholder="How did you find this student? "
      >
        <option
          v-for="option in referalLocations"
          :value="option"
          :key="option"
        >
          {{ option }}
        </option>
      </b-select>

      <b-field
        v-if="student.personalInformation.student_referal === 'Other (explain)'"
        label="Please provide more information"
        class="mt-2"
        :type="{ 'is-danger': hasError('student_referal_info') }"
        :message="hasError('student_referal_info')"
      >
        <b-input
          v-model="student.personalInformation.student_referal_info"
          name="student_referal_info"
          v-validate="{
            required:
              student.personalInformation.student_referal === 'Other (explain)',
          }"
          type="textarea"
        ></b-input>
      </b-field>
    </b-field>

    <div class="columns column is-full">
      <b-field
        label="UK Address"
        class="column is-one-third"
        :type="{ 'is-danger': hasError('house_number') }"
        :message="hasError('house_number')"
      >
        <b-input
          placeholder="House number"
          v-model="student.personalInformation.house_number"
          type="text"
          name="house_number"
        ></b-input>
      </b-field>

      <b-field
        label="Street"
        class="column is-one-third hide-label"
        :type="{ 'is-danger': hasError('street_address') }"
        :message="hasError('street_address')"
      >
        <b-input
          placeholder="Street"
          v-model="student.personalInformation.street_address"
          type="text"
          name="street_address"
        ></b-input>
      </b-field>

      <b-field
        label="City"
        class="column is-one-third hide-label"
        :type="{ 'is-danger': hasError('city') }"
        :message="hasError('city')"
      >
        <b-input
          placeholder="City"
          v-model="student.personalInformation.city"
          type="text"
          name="city"
        ></b-input>
      </b-field>

      <b-field
        label="Country"
        class="column is-one-third hide-label"
        :type="{ 'is-danger': hasError('country') }"
        :message="hasError('country')"
      >
        <b-autocomplete
          placeholder="Country"
          open-on-focus
          v-model="student.personalInformation.country"
          :data="filteredCountries('country')"
          icon="magnify"
          clearable
          @select="(option) => (student.personalInformation.country = option)"
        >
          <template #empty>No results found</template>
        </b-autocomplete>
      </b-field>

      <b-field
        label="Zip code"
        class="column is-one-third hide-label"
        :type="{ 'is-danger': hasError('zip_code') }"
        :message="hasError('zip_code')"
      >
        <b-input
          placeholder="Postcode"
          v-model="student.personalInformation.zip_code"
          type="text"
          name="zip_code"
        ></b-input>
      </b-field>
    </div>

    <b-field
      label="Delivery mode"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('delivery_mode') }"
      :message="hasError('delivery_mode')"
    >
      <b-autocomplete
        open-on-focus
        rounded
        v-model="student.personalInformation.delivery_mode"
        :data="filteredDelivery"
        icon="magnify"
        clearable
        @select="
          (option) => (student.personalInformation.delivery_mode = option)
        "
      >
        <template #empty>No results found</template>
      </b-autocomplete>
    </b-field>

    <b-field
      label="Timetable"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('timetable') }"
      :message="hasError('timetable')"
    >
      <template #label>
        Timetable
        <b-tooltip
          type="is-dark"
          label="Days and hours must be entered here - timetable chosen by the student"
        >
          <b-icon size="is-small" icon="help-circle-outline"></b-icon>
        </b-tooltip>
      </template>
      <b-input
        placeholder="Timetable"
        v-model="student.personalInformation.timetable"
        type="text"
        name="timetable"
      ></b-input>
    </b-field>

    <b-field
      label="Year of course"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('year_of_course') }"
      :message="hasError('year_of_course')"
    >
      <b-input
        placeholder="Year of course"
        v-model="student.personalInformation.year_of_course"
        type="text"
        name="year_of_course"
      ></b-input>
    </b-field>

    <b-field
      label="Interview date availability"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('interview_date_availability') }"
      :message="hasError('interview_date_availability')"
    >
      <b-input
        placeholder="(Mon to Fri 10:00 am to 3 pm) don´t write „any“"
        v-model="student.personalInformation.interview_date_availability"
        type="text"
        name="interview_date_availability"
      ></b-input>
    </b-field>

    <b-field
      v-if="
        student.personalInformation.emergency_contact &&
        student.personalInformation.emergency_contact.length
      "
      label="Emergency Contact"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('emergency_contact') }"
      :message="hasError('emergency_contact')"
    >
      <b-input
        placeholder="First Name + Phone Number, relationship with the student."
        v-model="student.personalInformation.emergency_contact"
        type="textarea"
        name="emergency_contact"
      ></b-input>
    </b-field>

    <div v-else class="columns column is-full">
      <b-field
        label="Emergency Contact Name"
        class="column is-one-third"
        :type="{ 'is-danger': hasError('emergency_contact_name') }"
        :message="hasError('emergency_contact_name')"
      >
        <b-input
          placeholder="Emergency Contact full name"
          v-model="student.personalInformation.emergency_contact_name"
          type="text"
          name="emergency_contact_name"
        ></b-input>
      </b-field>

      <b-field
        label="Emergency Contact phone number"
        class="column is-one-third"
        :type="{ 'is-danger': hasError('emergency_contact_phone') }"
        :message="hasError('emergency_contact_phone')"
      >
        <b-input
          placeholder="Emergency Contact phone number"
          v-model="student.personalInformation.emergency_contact_phone"
          type="text"
          name="emergency_contact_phone"
        ></b-input>
      </b-field>

      <b-field
        label="Emergency Contact relationship"
        class="column is-one-third"
        :type="{ 'is-danger': hasError('emergency_contact_relationship') }"
        :message="hasError('emergency_contact_relationship')"
      >
        <b-input
          placeholder="Relationship to student (mother, cousin etc.)"
          v-model="student.personalInformation.emergency_contact_relationship"
          type="text"
          name="emergency_contact_relationship"
        ></b-input>
      </b-field>
    </div>
  </div>
</template>

<script>
import {
  countryList,
  deliveryModes,
  genderOptions,
  maritalStatus,
  referalLocations,
} from '../../models/static';
import moment from 'moment';

export default {
  name: 'personal-information',
  props: ['studentPersonalInformation', 'formErrors'],
  data() {
    return {
      genderOptions: [],
      maritalStatus: [],
      referalLocations: [],
      student: {
        personalInformation: {
          date_of_birth: null,
          birth_country: '',

          gender: null,
          married: null,
          country: '',
          zip_code: '',
          house_number: '',
          street_address: '',
          city: '',
          delivery_mode: '',
          timetable: '',

          year_of_course: '',
          emergency_contact: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          emergency_contact_relationship: '',
          interview_date_availability: '',
          student_referal: '',
          student_referal_info: '',
        },
      },
    };
  },
  created() {
    this.genderOptions = genderOptions;
    this.maritalStatus = maritalStatus;
    this.referalLocations = referalLocations;
    let studentPersonalInformation = this.studentPersonalInformation;
    this.student.personalInformation = {
      ...studentPersonalInformation,
      date_of_birth: this.convertDate(studentPersonalInformation.date_of_birth),
    };
  },
  computed: {
    filteredDelivery() {
      if (!this.student.personalInformation.delivery_mode) {
        return deliveryModes;
      }

      return deliveryModes.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(
              this.student.personalInformation.delivery_mode.toLowerCase()
            ) >= 0
        );
      });
    },
  },
  watch: {
    student: {
      deep: true,
      handler: function (value) {
        this.$emit('changed', value);
      },
    },
  },
  methods: {
    convertDate(deadline) {
      if (deadline == null) {
        return deadline;
      } else if (new Date(deadline).toString() !== 'Invalid Date') {
        return moment(deadline).toDate();
      } else if (typeof deadline === 'string' || deadline instanceof String) {
        return moment(deadline, 'DD-MM-YYYY').toDate();
      }

      return null;
    },
    filteredCountries(field) {
      if (!field || !this.student.personalInformation[field]) {
        return countryList;
      }

      return countryList.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.student.personalInformation[field].toLowerCase()) >= 0
        );
      });
    },
    clearDate() {
      this.student.personalInformation.date_of_birth = null;
    },

    hasError(fieldName) {
      const fieldError = this.formErrors.filter(
        (error) => error.fieldName === fieldName
      );
      return fieldError.length ? fieldError[0].message : null;
    },
  },
};
</script>

<style>
.columns {
  flex-wrap: wrap;
}

.tabs li.is-active a,
.tabs li a:hover,
.tabs li.is-active a span {
  text-decoration: none;
}

.custom-autocomplete {
  height: fit-content;
}

.action-buttons {
  justify-content: center;
  display: flex;
}

.action-buttons button {
  margin: 0 10px;
}

.parent-holder {
  margin-bottom: 40px;
}

.card-holder {
  margin-top: 30px;
  margin-left: 0px;
}

.hide-label > label {
  visibility: hidden;
}

.hide-label:nth-child(4n) > label,
.hide-label:nth-child(4n + 1) > label {
  display: none;
}
</style>
